import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomerApi from './customerApi';
import { Customer } from './customerType';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GoogleMap, LoadScript, Marker, Autocomplete, Libraries } from '@react-google-maps/api';
import { validateEmail, validateAndFormatPhoneNumber } from '../lib/validator';
import { Save } from 'lucide-react';

const UpdateCustomerPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Customer', path: '/main/customer' },
    { label: 'Update', path: '/main/customer/update' },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location as { state: { customer: Customer } };
  const initialCustomer = state?.customer || {};

  const libraries = useRef<Libraries>(['places']);
  const [mapCenter, setMapCenter] = useState({
    lat: initialCustomer?.latitude || 28.0517,
    lng: initialCustomer?.longitude || -82.4588,
  });

  const [customer, setCustomer] = useState<Partial<Customer>>(initialCustomer);
  const [combinedAddress, setCombinedAddress] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

  // Initialize combined address
  useEffect(() => {
    const initialAddress = [
      initialCustomer.address_1?.trim(),
      initialCustomer.city?.trim(),
      initialCustomer.state?.trim(),
      initialCustomer.zip?.trim()
    ].filter(item => item && item !== '').join(', ');

    setCombinedAddress(initialAddress);
  }, [initialCustomer]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setCustomer({ ...customer, [name]: value });
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCombinedAddress(e.target.value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];
    setErrorMessage('');

    switch (name) {
      case 'phone':
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setCustomer(prev => ({ ...prev, phone: phoneResult.value }));
        }
        break;

      case 'email':
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setCustomer(prev => ({ ...prev, email: emailResult.value }));
        }
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage('');
    }
  };

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete(autocomplete);
  }, []);


  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });

        let address1 = '';
        let city = '';
        let state = '';
        let zip = '';

        if (place.address_components) {
          for (const component of place.address_components as google.maps.GeocoderAddressComponent[]) {
            const componentType = component.types[0];
            switch (componentType) {
              case 'street_number':
                address1 = `${component.long_name} ${address1}`;
                break;
              case 'route':
                address1 += component.short_name;
                break;
              case 'locality':
              case 'sublocality_level_1':
              case 'administrative_area_level_3':
                city = component.long_name;
                break;
              case 'administrative_area_level_1':
                state = component.long_name;
                break;
              case 'postal_code':
                zip = component.long_name;
                break;
            }
          }
        }

        const updatedAddress = [
          address1?.trim(),
          city?.trim(),
          state?.trim(),
          zip?.trim()
        ].filter(item => item && item !== '').join(', ');

        setCombinedAddress(updatedAddress);

        setCustomer({
          ...customer,
          address_1: address1.trim() === ',' ? '' : address1.trim(),
          city: city.trim() === ',' ? '' : city,
          state: state.trim() === ',' ? '' : state,
          zip: zip.trim() === ',' ? '' : zip,
          latitude: lat,
          longitude: lng,
        });
      }
    }
  }, [autocomplete, customer]);


  const validateForm = (): boolean => {
    setErrorMessage('');

    const phoneResult = validateAndFormatPhoneNumber(customer.phone || '');
    if (!phoneResult.isValid) {
      setErrorMessage(phoneResult.error || 'Invalid phone number');
      return false;
    }

    const emailResult = validateEmail(customer.email || '');
    if (!emailResult.isValid) {
      setErrorMessage(emailResult.error || 'Invalid email address');
      return false;
    }

    if (!customer.city || !customer.city.trim()) {
      setErrorMessage('City is required. Please select a full address from the suggestions (e.g. "123 Main St, Lutz, FL").');
      return false;
    }

    if (!customer.state || !customer.state.trim()) {
      setErrorMessage('State is required. Please select a full address from the suggestions (e.g. "123 Main St, Lutz, FL").');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await CustomerApi.update(customer.customer_id as string, customer as Customer);
      setSuccessMessage('Customer updated successfully');

      // Optional: Navigate back after a delay
      //setTimeout(() => {
      //  navigate('/main/customer');
      //}, 2000);
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('An unknown error occurred.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <h1 className="text-2xl font-semibold text-foreground mb-6">
            Update Customer
          </h1>

          {/* Success Message */}
          {successMessage && (
            <div>
              <Alert variant="success">
                {successMessage}
              </Alert>
              <div className="flex gap-4">
                <Button
                  type="button"
                  onClick={() => navigate('/main/customer')}
                  size="medium" variant="default"
                >
                  Back to Customers
                </Button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {!successMessage && errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {/* Form - Only show if no success message */}
          {!successMessage && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-foreground mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={customer.first_name || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter first name"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-foreground mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={customer.last_name || ''}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter last name"
                  />
                </div>

                {/* Company Name */}
                <div>
                  <label htmlFor="customer_company_name" className="block text-sm font-medium text-foreground mb-2">
                    Company Name
                  </label>
                  <Input
                    type="text"
                    id="customer_company_name"
                    name="customer_company_name"
                    value={customer.customer_company_name || ''}
                    onChange={handleInputChange}
                    placeholder="Enter company name (optional)"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={customer.email || ''}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    placeholder="Enter email address"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={customer.phone || ''}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    required
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              {/* Address Section */}
              <div className="space-y-4">
                <label htmlFor="address" className="block text-sm font-medium text-foreground">
                  Customer Address <span className="text-red-500">*</span>
                </label>

                <LoadScript
                  googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}
                  libraries={libraries.current}
                >
                  <div className="space-y-4">
                    <Autocomplete
                      onLoad={onLoad}
                      onPlaceChanged={onPlaceChanged}
                      options={{
                        types: ['address'],
                        componentRestrictions: { country: 'us' },
                      }}
                    >
                      <Input
                        type="text"
                        placeholder="Search for a location"
                        value={combinedAddress}
                        onChange={handleAddressChange}
                        required
                      />
                    </Autocomplete>

                    {/* Save Button */}
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      size="medium" variant="default"
                    >
                      <Save size={16} />
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </Button>

                    {/* Google Map */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <GoogleMap
                        center={mapCenter}
                        zoom={14}
                        mapContainerStyle={{ height: '400px', width: '100%' }}
                      >
                        <Marker position={mapCenter} />
                      </GoogleMap>
                    </div>
                  </div>
                </LoadScript>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default UpdateCustomerPage;